<template>
  <div class="ui-street-suggest">
    <ui-input
      v-model="query"
      ref="query"
      :label="label"
      autocomplete="off"
      :placeholder="placeholder"
      :required="required"
      validate="street"
      clearable
      @input="getSuggestions"
      @focus="onFocus"
      @blur="onBlur"
    />

    <div class="ui-street-suggest__suggestions">
      <ui-dropdown
        :visible="showSuggestions"
      >
        <div
          v-for="(suggestion, ind) of formattedSuggestions"
          :key="ind"
          class="ui-street-suggest__suggestion"
          @click="select(suggestion)"
        >
          {{ suggestion.text }}

          <f-sprite-icon
            v-if="suggestion.id === value.id"
            :icon-link="$options.icons.IconCheck.id"
          />
        </div>
      </ui-dropdown>
    </div>
  </div>
</template>

<script>
import { debounce } from 'throttle-debounce';

import IconCheck from '@icons/check.svg';

const addressToString = ({ kind, name } = {}) => {
  switch (kind) {
    case 'Проспект': return `${name} пр-т`;
    case 'Проезд': return `${name} пр.`;
    case 'Улица': return `ул. ${name}`;
    case 'Переулок': return `${name} пер.`;
    case 'Набережная': return `наб. ${name}`;
    case 'Тупик': return `${name} туп.`;
    case undefined: return name;
    default: return `${kind}, ${name}`;
  }
};

export default {
  name: 'ui-street-suggest',

  icons: {
    IconCheck,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },

    required: {
      type: Boolean,
      default: false,
    },

    cityId: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: 'Название улицы',
    },

    label: {
      type: String,
      default: 'Улица *',
    },
  },

  data: () => ({
    isMounted: false,
    query: '',
    suggestions: [],
    isSelectedFromSuggest: false,
    isFocused: false,
  }),

  computed: {
    formattedSuggestions() {
      return this.suggestions.map(s => ({
        ...s,
        text: addressToString(s),
      }));
    },
    showSuggestions() {
      return this.isMounted && this.isFocused && !!this.suggestions.length;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.query = val?.name;
      },
    },
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    select(street) {
      if (street) {
        this.isSelectedFromSuggest = true;
        this.$emit('input', street);
      }
    },

    getSuggestions: debounce(300, false, function (query = this.query) {
      if (query.length < 2 || !this.cityId) {
        if (this.suggestions.length) this.suggestions = [];
        return;
      }
      this.$store.$api.findStreet({ query, cityId: this.cityId }).then(streets => {
        this.suggestions = streets;
      }).catch(() => {
        console.warn(`По запросу "${this.query}" ничего не найдено`);
      });
    }),

    // для внешнего вызова
    checkValidOutside() {
      if (this.$refs.query) return this.$refs.query.checkValidOutside();
    },

    onFocus() {
      this.isFocused = true;
      this.getSuggestions();
    },

    onBlur() {
      if (!this.isSelectedFromSuggest && this.query !== this.value?.name) {
        this.$emit('input', { name: this.query });
      }
      this.isFocused = false;
      this.isSelectedFromSuggest = false;
      this.suggestions = [];
    },
  },
};
</script>

<style lang="scss">
.ui-street-suggest {
  position: relative;

  &__suggestions {
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;

    .ui-dropdown {
      margin-top: 8px;
      width: 100%;
    }

    .ui-dropdown-scrollview {
      max-height: 216px;
    }
  }

  &__suggestion {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0;
    padding: 8px 12px;
    line-height: 24px;
    cursor: pointer;

    &:hover {
      background-color: $gray_bgr;
    }

    .sprite-icon {
      width: 16px;
      min-width: 16px;
      height: 16px;
      margin-left: 5px;
      color: $gray_l;
      transition: .2s opacity ease;
    }
  }
}
</style>
